import { useEffect, useState } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';

/**
 * Admin theme (redesign phase 3): "Sistema" follows prefers-color-scheme, or the viewer picks Claro /
 * Escuro. The choice is a per-viewer convenience (localStorage, may be unavailable); the resolved theme
 * is set as data-theme on <html> only while the admin is mounted, so the site, the merchant app and the
 * storefront never change.
 */
export type ThemePref = 'system' | 'light' | 'dark';
export const THEME_KEY = 'perzo.admin.theme';

export function readThemePref(): ThemePref {
  try {
    const v = window.localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

function prefersDark(): boolean {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

export function resolveTheme(pref: ThemePref, systemDark: boolean): 'light' | 'dark' {
  return pref === 'system' ? (systemDark ? 'dark' : 'light') : pref;
}

export function useAdminTheme(active: boolean): [ThemePref, (p: ThemePref) => void] {
  const [pref, setPrefState] = useState<ThemePref>(() => (typeof window === 'undefined' ? 'system' : readThemePref()));
  const [systemDark, setSystemDark] = useState(() => (typeof window === 'undefined' ? false : prefersDark()));

  useEffect(() => {
    if (!active || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const root = document.documentElement;
    root.dataset.theme = resolveTheme(pref, systemDark);
    return () => {
      delete root.dataset.theme;
    };
  }, [active, pref, systemDark]);

  const setPref = (p: ThemePref) => {
    setPrefState(p);
    try {
      if (p === 'system') window.localStorage.removeItem(THEME_KEY);
      else window.localStorage.setItem(THEME_KEY, p);
    } catch {
      /* per-viewer convenience only */
    }
  };
  return [pref, setPref];
}

const OPTIONS: Array<{ value: ThemePref; label: string; Icon: typeof Sun }> = [
  { value: 'system', label: 'Sistema', Icon: Monitor },
  { value: 'light', label: 'Claro', Icon: Sun },
  { value: 'dark', label: 'Escuro', Icon: Moon },
];

export function ThemeSwitch({ pref, onChange }: { pref: ThemePref; onChange: (p: ThemePref) => void }) {
  return (
    <div className="theme-switch" role="group" aria-label="Tema do admin">
      {OPTIONS.map(({ value, label, Icon }) => (
        <button key={value} type="button" aria-pressed={pref === value} onClick={() => onChange(value)} title={`Tema: ${label}`}>
          <Icon size={14} aria-hidden="true" />
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
}
